import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { parsePrice } from "@/components/shared";
import { hasPrivilege, getPrivilegeToken } from "@/lib/privilege";
import { HandCoins, X, Plus, ShieldCheck, CheckCircle2 } from "lucide-react";

const fmtP = (n) => n.toLocaleString("tr-TR", { maximumFractionDigits: 2 });

const buildMessage = (col, req, price) => {
  const cur = col.offer?.currency || "TRY";
  return `Sayın ${col.supplier.contact_name || col.supplier.company_name},

${req.title} ihalesi için vermiş olduğunuz ${fmtP(col.offer.price)} ${cur} tutarındaki teklifinize karşılık ${price ? fmtP(price) : "____"} ${cur} fiyatını öneriyoruz.

Aşağıdaki bağlantıdan önerimizi inceleyip kabul veya ret yanıtınızı iletebilirsiniz.

ÖNEMLİ: Teklifinizde malzeme detayları satır satır yazılmış olsa dahi, güncel teklif dosyalarınızı (PDF/Excel) mutlaka sisteme yüklemenizi rica ederiz.

Saygılarımızla,
GettProposal`;
};

const EmailListInput = ({ label, list, setList, recipients, tid }) => {
  const [free, setFree] = useState("");
  const add = (email) => {
    const e = email.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return toast.error("Geçerli bir e-posta girin");
    if (!list.includes(e)) setList([...list, e]);
    setFree("");
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
      {list.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {list.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs" data-testid={`${tid}-chip-${e}`}>
              {e}
              <button onClick={() => setList(list.filter((x) => x !== e))} className="text-slate-400 hover:text-red-500" data-testid={`${tid}-remove-${e}`}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <Select value="" onValueChange={add}>
          <SelectTrigger data-testid={`${tid}-select`} className="rounded-none h-9 border-slate-200 flex-1 text-xs">
            <SelectValue placeholder="Kullanıcı seç" />
          </SelectTrigger>
          <SelectContent className="rounded-none max-h-60">
            {recipients.map((u) => (
              <SelectItem key={u.id} value={u.email}>{u.name} ({u.email})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input value={free} onChange={(e) => setFree(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(free); } }} placeholder="veya e-posta yazın" data-testid={`${tid}-free-input`} className="rounded-none h-9 border-slate-200 flex-1 text-xs" />
        <Button variant="outline" onClick={() => add(free)} data-testid={`${tid}-add-button`} className="rounded-none h-9 border-slate-200 hover:bg-slate-100 px-3"><Plus size={13} /></Button>
      </div>
    </div>
  );
};

export const PriceProposalModal = ({ col, req, onClose, onSent }) => {
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [dirty, setDirty] = useState(false);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!col) return;
    setPrice(""); setCc([]); setBcc([]); setDirty(false);
    setMessage(buildMessage(col, req, null));
    api.get("/approvals/recipients").then((r) => setRecipients(r.data)).catch(() => {});
  }, [col]);

  useEffect(() => {
    if (!col || dirty) return;
    const p = parsePrice(price);
    setMessage(buildMessage(col, req, p && p > 0 ? p : null));
  }, [price]);

  if (!col) return null;
  const cur = col.offer?.currency || "TRY";

  const send = async () => {
    const p = parsePrice(price);
    if (!p || p <= 0) return toast.error("Geçerli bir fiyat girin");
    if (!message.trim()) return toast.error("E-posta metni boş olamaz");
    setSending(true);
    try {
      await api.post("/price-proposals", { offer_id: col.offer.id, proposed_price: p, message, cc, bcc });
      toast.success(`Fiyat önerisi ${col.supplier.contact_email} adresine gönderildi`);
      onSent();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!col} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="price-proposal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <HandCoins size={18} className="text-amber-600" /> Tedarikçiye Fiyat Öner
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="border border-slate-200 bg-slate-50 p-3 text-sm">
            <div className="font-semibold">{col.supplier.company_name}</div>
            <div className="text-slate-500 text-xs mt-0.5">Mevcut teklif: <span className="font-semibold text-slate-900">{fmtP(col.offer.price)} {cur}</span></div>
          </div>
          <div className="space-y-1.5">
            <Label>Önerilen Fiyat ({cur})</Label>
            <Input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="proposal-price-input" className="rounded-none border-slate-200 h-11" />
            {String(price).trim() && !isNaN(parsePrice(price)) && (
              <p className="text-xs text-emerald-700 font-medium">= {parsePrice(price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {cur}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Alıcı (Tedarikçi)</Label>
            <Input value={col.supplier.contact_email} readOnly disabled data-testid="proposal-to-input" className="rounded-none border-slate-200 h-9 bg-slate-50 text-xs" />
          </div>
          <EmailListInput label="CC" list={cc} setList={setCc} recipients={recipients} tid="proposal-cc" />
          <EmailListInput label="BCC" list={bcc} setList={setBcc} recipients={recipients} tid="proposal-bcc" />
          <div className="space-y-1.5">
            <Label>E-posta Metni</Label>
            <Textarea value={message} onChange={(e) => { setMessage(e.target.value); setDirty(true); }} rows={11} data-testid="proposal-message-input" className="rounded-none border-slate-200 text-sm" />
            <p className="text-[11px] text-slate-500">E-postanın altına tedarikçiye özel "Fiyat Önerisini İncele ve Yanıtla" butonu otomatik eklenir.</p>
          </div>
          <Button onClick={send} disabled={sending} data-testid="proposal-send-button" className="w-full rounded-none h-11 bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
            {sending ? "Gönderiliyor..." : "Fiyat Önerisini Gönder"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ApplyProposalDialog = ({ proposal, company, onClose, onApplied }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState("");
  if (!proposal) return null;
  const priv = hasPrivilege();
  const isCounter = proposal.status === "supplier_countered";
  const amount = isCounter ? proposal.counter_price : proposal.proposed_price;

  const apply = async () => {
    if (!priv && !pw) return toast.error("Süper şifreyi girin");
    setBusy("apply");
    try {
      const payload = priv ? { privilege_token: getPrivilegeToken() } : { super_password: pw };
      await api.post(`/price-proposals/${proposal.id}/apply`, payload);
      toast.success("Yeni fiyat uygulandı");
      onApplied();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const reject = async () => {
    setBusy("reject");
    try {
      await api.post(`/price-proposals/${proposal.id}/reject`);
      toast.success("Öneri reddedildi, tedarikçi bilgilendirildi");
      onApplied();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={!!proposal} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-md" data-testid="apply-proposal-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <CheckCircle2 size={18} className="text-emerald-600" /> {isCounter ? "Karşı Tutarı Değerlendir" : "Son Onay — Fiyatı Uygula"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            {isCounter ? (
              <>
                <strong>{company}</strong>, belirtilen <strong>{fmtP(proposal.proposed_price)} {proposal.currency}</strong> tutarı yerine{" "}
                <strong className="text-blue-700">{fmtP(proposal.counter_price)} {proposal.currency}</strong> önerdi.
              </>
            ) : (
              <>
                <strong>{company}</strong>, önerdiğiniz <strong>{fmtP(proposal.proposed_price)} {proposal.currency}</strong> fiyatını kabul etti
                (mevcut: {fmtP(proposal.current_price)} {proposal.currency}).
              </>
            )}
            {proposal.supplier_note && <> Tedarikçi notu: "{proposal.supplier_note}"</>}
            {" "}Onayınızla teklif fiyatı <strong>{fmtP(amount)} {proposal.currency}</strong> olarak güncellenecek.
          </p>
          {priv ? (
            <p className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="apply-privilege-note"><ShieldCheck size={12} /> Özel Yetki aktif — süper şifre gerekmez. İşlem kayıt altına alınır.</p>
          ) : (
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 text-amber-700"><ShieldCheck size={13} /> Süper Şifre</Label>
              <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Fiyat uygulamak süper şifre gerektirir" data-testid="apply-super-password-input" className="rounded-none border-amber-300 h-10 bg-amber-50/50" />
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={apply} disabled={!!busy || (!priv && !pw)} data-testid="apply-proposal-button" className="flex-1 rounded-none h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors duration-200">
              {busy === "apply" ? "Uygulanıyor..." : isCounter ? "Kabul Et ve Uygula" : "Fiyatı Uygula"}
            </Button>
            <Button variant="outline" onClick={reject} disabled={!!busy} data-testid="reject-proposal-button" className="flex-1 rounded-none h-11 border-red-500/30 text-red-500 hover:bg-red-500/10 font-semibold transition-colors duration-200">
              {busy === "reject" ? "Reddediliyor..." : "Reddet"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
